import { 
	AiCostSummary,
	AiCostTrendsData,
	AiCostsSummaryData,
	UserAiCostSummary,
	NotificationResponse
} from '../types';
import { logger } from '../utils/logger';

// API configuration
// For HTTPS frontend (e.g. https://app.renegadewls.com), backend must be HTTPS to avoid Mixed Content.
// Set REACT_APP_API_BASE_URL in CI/CD when building for deploy; for local dev use .env (e.g. http://0.0.0.0:2004).
export const API_BASE_URL =
	process.env.REACT_APP_API_BASE_URL ||
	process.env.REACT_APP_API_URL ||
	'https://api.renegadewls.com';


// API endpoints
export const API_ENDPOINTS = {
	auth: {
		signin: `${API_BASE_URL}/auth/signin`,
		signup: `${API_BASE_URL}/auth/signup`,
		me: `${API_BASE_URL}/auth/me`,
		lastLogin: `${API_BASE_URL}/auth/last-login`,
		changePassword: `${API_BASE_URL}/auth/change-password`,
		forgotPasswordSendLink: `${API_BASE_URL}/auth/forgot-password/send-link`,
		forgotPasswordReset: `${API_BASE_URL}/auth/forgot-password/reset-password`,
		setPassword: `${API_BASE_URL}/auth/set-password`,
	},
	documents: {
		upload: `${API_BASE_URL}/uploadExel`,
		ask: `${API_BASE_URL}/ask`,
		askDetailed: `${API_BASE_URL}/ask/detailed`,
		askFollowup: `${API_BASE_URL}/ask/followup`,
		status: `${API_BASE_URL}/status`,
	},
	chat: {
		getHistory: `${API_BASE_URL}/chat/history`,
		saveHistory: `${API_BASE_URL}/chat/history`,
		feedback: `${API_BASE_URL}/chat/feedback`,
	},
	chatSessions: {
		create: `${API_BASE_URL}/chat/sessions`,
		list: `${API_BASE_URL}/chat/sessions`,
		get: (id: string) => `${API_BASE_URL}/chat/sessions/${id}`,
		appendMessages: (id: string) => `${API_BASE_URL}/chat/sessions/${id}/messages`,
		rename: (id: string) => `${API_BASE_URL}/chat/sessions/${id}`,
		delete: (id: string) => `${API_BASE_URL}/chat/sessions/${id}`,
	},
	chatLogs: {
		list: `${API_BASE_URL}/chat/logs`,
		create: `${API_BASE_URL}/chat/logs`,
		createSession: `${API_BASE_URL}/chat/logs/session`,
		get: (id: string) => `${API_BASE_URL}/chat/logs/${id}`,
		export: `${API_BASE_URL}/chat/logs/export`,
	},
	adminChatSessions: {
		list: `${API_BASE_URL}/admin/chat-sessions`,
		get: (id: string) => `${API_BASE_URL}/admin/chat-sessions/${id}`,
		export: `${API_BASE_URL}/admin/chat-sessions/export`,
	},
	adminFeedback: {
		list: `${API_BASE_URL}/admin/feedback`,
	},
	// Admin settings
	adminSettings: {
		maxDailyMessages: `${API_BASE_URL}/admin/settings/max-daily-messages`,
		speechToText: `${API_BASE_URL}/admin/settings/speech-to-text`,
		allowManagersCreateUsers: `${API_BASE_URL}/admin/settings/allow-managers-create-users`,
		systemEmail: `${API_BASE_URL}/admin/settings/system-email`,
		testSystemEmail: `${API_BASE_URL}/admin/settings/system-email/test`,
		notifications: `${API_BASE_URL}/admin/settings/notifications`,
	},
	// Public settings (accessible to all authenticated users)
	settings: {
		speechToText: `${API_BASE_URL}/settings/speech-to-text`,
	},
	// Notifications
	notifications: {
		list: `${API_BASE_URL}/notifications`,
		markRead: (id: string) => `${API_BASE_URL}/notifications/${id}/read`,
		markAllRead: `${API_BASE_URL}/notifications/mark-all-read`,
		settings: `${API_BASE_URL}/notifications/settings`,
	},
	// Test Notifications (Admin only)
	testNotifications: {
		sendSopAdded: `${API_BASE_URL}/admin/test-notifications/send-sop-added`,
		sendUserOnboarded: `${API_BASE_URL}/admin/test-notifications/send-user-onboarded`,
		sendGeneral: `${API_BASE_URL}/admin/test-notifications/send-general`,
		getStats: `${API_BASE_URL}/admin/test-notifications/stats`,
	},
	// AI Cost tracking
	aiCosts: {
		currentMonth: `${API_BASE_URL}/admin/ai-costs/current-month`,
		monthly: (year: number, month: number) => `${API_BASE_URL}/admin/ai-costs/monthly/${year}/${month}`,
		trends: `${API_BASE_URL}/admin/ai-costs/trends`,
		user: (userEmail: string) => `${API_BASE_URL}/admin/ai-costs/user/${userEmail}`,
		summary: `${API_BASE_URL}/admin/ai-costs/summary`,
	},
	dashboard: {
		stats: `${API_BASE_URL}/dashboard/stats`,
		documentUsageStats: `${API_BASE_URL}/admin/document-usage-stats`,
		managerStats: `${API_BASE_URL}/manager/dashboard/stats`,
	},
	// New endpoints for SOP management
	sops: {
		list: `${API_BASE_URL}/sops`, // GET /sops?page=1&page_size=10&search=term
		create: `${API_BASE_URL}/sops`, // POST
		get: (id: string) => `${API_BASE_URL}/sops/${id}`, // GET
		update: (id: string) => `${API_BASE_URL}/sops/${id}`, // PUT
		delete: (id: string) => `${API_BASE_URL}/sops/${id}`, // DELETE
		processingStatus: (fileId: string) =>
			`${API_BASE_URL}/sops/${fileId}/processing-status`,
	},
	// New uploads listing endpoint (merged uploads + metadata)
	uploads: {
		list: `${API_BASE_URL}/uploads`, // GET /uploads?page=1&page_size=10&search=term
	},
	// Admin users CRUD
	users: {
		list: `${API_BASE_URL}/admin/users`, // GET with query params
		create: `${API_BASE_URL}/admin/users`, // POST
		update: (id: string) => `${API_BASE_URL}/admin/users/${id}`, // PUT
		updateStatus: (id: string) => `${API_BASE_URL}/admin/users/${id}/status`, // PATCH ?status=active|inactive
		updatePassword: (id: string) => `${API_BASE_URL}/admin/users/${id}/password`, // PUT
		delete: (id: string) => `${API_BASE_URL}/admin/users/${id}`, // DELETE
	},
	// Manager worker management
	managerWorkers: {
		list: `${API_BASE_URL}/manager/workers`, // GET with query params
		create: `${API_BASE_URL}/manager/workers`, // POST
		updatePassword: (id: string) => `${API_BASE_URL}/manager/workers/${id}/password`, // PUT
		updateStatus: (id: string) => `${API_BASE_URL}/manager/workers/${id}/status`, // PATCH
	},
	// Manager chat logs
	managerChatLogs: {
		list: `${API_BASE_URL}/manager/chat-logs`, // GET with query params
		get: (id: string) => `${API_BASE_URL}/manager/chat-logs/${id}`, // GET
	},
};

// Helper function to escape regex special characters
// This prevents MongoDB regex errors when special characters are used in search
const escapeRegexSpecialChars = (str: string): string => {
	if (!str) return str;
	// Escape special regex characters: \ ^ $ . | ? * + ( ) [ ] { }
	return str.replace(/[\\^$.*+?()[\]{}|]/g, '\\$&');
};

// Helper function to get auth headers
export const getAuthHeaders = () => {
	const token = localStorage.getItem('access_token');
	return {
		'Content-Type': 'application/json',
		...(token && { Authorization: `Bearer ${token}` }),
	};
};

// Global logout handler for unauthorized access
let globalLogoutHandler: ((reason?: string) => void) | null = null;
let isLoggingOut = false; // Flag to prevent multiple logout calls

export const setGlobalLogoutHandler = (logoutHandler: (reason?: string) => void) => {
	globalLogoutHandler = logoutHandler;
};

// Helper function for API calls
export const apiCall = async (url: string, options: RequestInit = {}) => {
	const response = await fetch(url, {
		...options,
		headers: {
			...getAuthHeaders(),
			...options.headers,
		},
	});

	if (!response.ok) {
		// Parse error response first
		const error = await response
			.json()
			.catch(() => ({ detail: 'An error occurred' }));
		
		// Handle 401 Unauthorized responses
		if (response.status === 401) {
			// Clear the token from localStorage
			localStorage.removeItem('access_token');
			
			// Call the global logout handler if available, but only once
			if (globalLogoutHandler && !isLoggingOut) {
				isLoggingOut = true;
				globalLogoutHandler('session-timeout');
				// Reset the flag after a short delay to allow for future logouts
				setTimeout(() => {
					isLoggingOut = false;
				}, 1000);
			} else if (!globalLogoutHandler) {
				// Fallback: redirect to login page
				window.location.href = '/login';
			}
			
			// Throw an error to prevent further execution
			throw new Error('Unauthorized access. Please login again.');
		}
		
		// Handle 403 Forbidden for inactive/deactivated users
		if (response.status === 403) {
			const errorDetail = error.detail || '';
			// Check if the error message indicates account deactivation
			if (errorDetail.toLowerCase().includes('deactivated') || 
			    errorDetail.toLowerCase().includes('inactive')) {
				// Clear the token from localStorage
				localStorage.removeItem('access_token');
				
				// Call the global logout handler if available, but only once
				if (globalLogoutHandler && !isLoggingOut) {
					isLoggingOut = true;
					globalLogoutHandler('account-deactivated');
					// Reset the flag after a short delay to allow for future logouts
					setTimeout(() => {
						isLoggingOut = false;
					}, 1000);
				} else if (!globalLogoutHandler) {
					// Fallback: redirect to login page
					window.location.href = '/login';
				}
				
				// Throw the original error message
				throw new Error(errorDetail);
			}
		}
		
		throw new Error(error.detail || `HTTP error! status: ${response.status}`);
	}

	return response.json();
};

// ------------------------------
// SOPs
// ------------------------------
export interface FetchSopsParams {
	page?: number;
	pageSize?: number;
	search?: string;
}

export const fetchSops = async (params: FetchSopsParams = {}) => {
	const { page = 1, pageSize = 10, search = '' } = params;
    // Use canonical SOPs endpoint so only SOPs with metadata (title/description/roles/users) appear
    const url = new URL(API_ENDPOINTS.sops.list);
	url.searchParams.set('page', String(page));
	url.searchParams.set('page_size', String(pageSize));
	if (search) url.searchParams.set('search', escapeRegexSpecialChars(search));
	return apiCall(url.toString());
};

// ------------------------------
// Admin Users
// ------------------------------

export type AdminUserRole = 'admin' | 'manager' | 'worker';
export type AdminUserStatus = 'active' | 'inactive';

export type AdminUser = {
    id: string;
    name: string;
    email: string;
    role: AdminUserRole;
    assignedManager?: string | null;
    status: AdminUserStatus;
    grantSopAccess?: boolean;
};

export const fetchUsers = async (params: { page?: number; pageSize?: number; search?: string; role?: 'all' | AdminUserRole; status?: AdminUserStatus } = {}): Promise<{ items: AdminUser[]; total: number }> => {
    const { page = 1, pageSize = 10, search = '', role, status } = params;
    const url = new URL(API_ENDPOINTS.users.list);
    url.searchParams.set('page', String(page));
    url.searchParams.set('page_size', String(pageSize));
    if (search) url.searchParams.set('search', escapeRegexSpecialChars(search));
    if (role) url.searchParams.set('role', role);
    if (status) url.searchParams.set('status', status);
    return apiCall(url.toString());
};

export const createUser = async (payload: {
    name: string;
    email: string;
    role: AdminUserRole;
    assignedManager?: string | null;
    status?: AdminUserStatus;
    grant_sop_access?: boolean;
}): Promise<{ item: AdminUser }> => {
    return apiCall(API_ENDPOINTS.users.create, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
};

export const updateUser = async (
    id: string,
    payload: Partial<{
        name: string;
        email: string;
        role: AdminUserRole;
        assignedManager?: string | null;
        status?: AdminUserStatus;
        grant_sop_access?: boolean;
    }>,
): Promise<{ item: AdminUser }> => {
    return apiCall(API_ENDPOINTS.users.update(id), {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
};

export const updateUserStatus = async (id: string, status: AdminUserStatus): Promise<{ item: AdminUser }> => {
    const url = new URL(API_ENDPOINTS.users.updateStatus(id));
    url.searchParams.set('status', status);
    return apiCall(url.toString(), { method: 'PATCH' });
};

export const updateUserPassword = async (id: string, newPassword: string, confirmPassword: string): Promise<{ message: string }> => {
    return apiCall(API_ENDPOINTS.users.updatePassword(id), {
        method: 'PUT',
        body: JSON.stringify({ new_password: newPassword, confirm_password: confirmPassword }),
    });
};

export const updateManagerWorkerPassword = async (workerId: string, newPassword: string, confirmPassword: string): Promise<{ message: string }> => {
    return apiCall(API_ENDPOINTS.managerWorkers.updatePassword(workerId), {
        method: 'PUT',
        body: JSON.stringify({ new_password: newPassword, confirm_password: confirmPassword }),
    });
};

export const updateManagerWorkerStatus = async (workerId: string, status: 'active' | 'inactive'): Promise<{ message: string }> => {
    return apiCall(API_ENDPOINTS.managerWorkers.updateStatus(workerId), {
        method: 'PATCH',
        body: JSON.stringify({ status }),
    });
};

export const deleteUser = async (id: string, replacementManagerId?: string): Promise<{ 
    message: string; 
    reassigned_count?: number; 
    replacement_manager?: { id: string; name: string; email: string }; 
    direct_delete?: boolean; 
    success?: boolean; 
    user_id?: string; 
    sop_cleanup_count?: number;
    cleanup_summary?: {
        sop_references_cleaned: number;
        chat_history_deleted: number;
        chat_logs_deleted: number;
        chat_sessions_deleted: number;
        notifications_deleted: number;
        fcm_tokens_deleted: number;
        daily_counts_deleted: number;
    }
}> => {
    return apiCall(API_ENDPOINTS.users.delete(id), { 
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ replacementManagerId })
    });
};

// Enhanced API functions for improved question answering
export type AskResponseStatus =
	| 'SUCCESS'
	| 'NO_SOPS_ASSIGNED'
	| 'NO_PROCESSED_DOCUMENTS'
	| 'SOPS_PROCESSING'
	| 'NO_RELEVANT_CONTENT'
	| 'OFF_TOPIC'
	| 'CHITCHAT'
	| 'ERROR';

export interface AskQuestionResponse {
	answer: string;
	status?: AskResponseStatus;
}

export const askQuestion = async (question: string) => {
	return apiCall(API_ENDPOINTS.documents.ask, {
		method: 'POST',
		body: JSON.stringify({ question }),
	});
};

export const askDetailedQuestion = async (
	question: string,
	analysisType: string = 'comprehensive',
	sessionId?: string
) => {
	return apiCall(API_ENDPOINTS.documents.askDetailed, {
		method: 'POST',
		body: JSON.stringify({
			question,
			analysis_type: analysisType,
			session_id: sessionId,
		}),
	});
};

export const askFollowupQuestion = async (
	question: string,
	previousContext: string = '',
	strategy: string = 'comprehensive',
	sessionId?: string
) => {
	return apiCall(API_ENDPOINTS.documents.askFollowup, {
		method: 'POST',
		body: JSON.stringify({
			question,
			previous_context: previousContext,
			strategy,
			session_id: sessionId,
		}),
	});
};

export const uploadDocument = async (file: File) => {
	const formData = new FormData();
	formData.append('file', file);

	const token = localStorage.getItem('access_token');
	const uploadUrl = API_ENDPOINTS.documents.upload;
	logger.log('[SOP Upload] POST upload', { url: uploadUrl, filename: file.name });
	const response = await fetch(uploadUrl, {
		method: 'POST',
		headers: {
			...(token && { Authorization: `Bearer ${token}` }),
		},
		body: formData,
	},
	);

	if (!response.ok) {
		// Parse error response first
		const error = await response
			.json()
			.catch(() => ({ detail: 'An error occurred' }));
	
		if (response.status === 401) {
			localStorage.removeItem('access_token');
			if (globalLogoutHandler && !isLoggingOut) {
				isLoggingOut = true;
				globalLogoutHandler('session-timeout');
				// Reset the flag after a short delay to allow for future logouts
				setTimeout(() => {
					isLoggingOut = false;
				}, 1000);
			} else if (!globalLogoutHandler) {
				// Fallback: redirect to login page
				window.location.href = '/login';
			}
			
			// Throw an error to prevent further execution
			throw new Error('Unauthorized access. Please login again.');
		}
		
		// Handle 403 Forbidden for inactive/deactivated users
		if (response.status === 403) {
			const errorDetail = error.detail || '';
			// Check if the error message indicates account deactivation
			if (errorDetail.toLowerCase().includes('deactivated') || 
			    errorDetail.toLowerCase().includes('inactive')) {
				// Clear the token from localStorage
				localStorage.removeItem('access_token');
				
				// Call the global logout handler if available, but only once
				if (globalLogoutHandler && !isLoggingOut) {
					isLoggingOut = true;
					globalLogoutHandler('account-deactivated');
					// Reset the flag after a short delay to allow for future logouts
					setTimeout(() => {
						isLoggingOut = false;
					}, 1000);
				} else if (!globalLogoutHandler) {
					// Fallback: redirect to login page
					window.location.href = '/login';
				}
				
				// Throw the original error message
				throw new Error(errorDetail);
			}
		}
		
		throw new Error(error.detail || `HTTP error! status: ${response.status}`);
	}

	const data = await response.json();
	logger.log('[SOP Upload] response', { file_id: data.file_id, url: uploadUrl, status: data.status });
	return data;
};

// Create/Update/Delete SOP metadata
export interface UpsertSopPayload {
    file_id?: string; // required for create
    title: string;
    description: string;
    user_role: string;
    specific_users: string[];
	file_name?: string;
}

export type SopProcessingStatusValue =
	| 'pending'
	| 'processing'
	| 'completed'
	| 'failed'
	| 'deleted'
	| 'unknown';

export interface SopProcessingStatusResponse {
	file_id: string;
	processing_status: SopProcessingStatusValue;
	filename?: string;
	processing_error?: string | null;
}

export const fetchSopProcessingStatus = async (
	fileId: string,
): Promise<SopProcessingStatusResponse> => {
	return apiCall(API_ENDPOINTS.sops.processingStatus(fileId));
};

export const createSop = async (payload: UpsertSopPayload) => {
    const url = API_ENDPOINTS.sops.create;
    logger.log('[SOP Create] POST createSop', { url, file_id: payload.file_id, title: payload.title });
    try {
        const result = await apiCall(url, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
        logger.log('[SOP Create] success', { file_id: payload.file_id });
        return result;
    } catch (err: unknown) {
        const e = err as { message?: string; response?: { status?: number }; status?: number };
        logger.error('[SOP Create] error', { url, file_id: payload.file_id, message: e?.message, status: e?.response?.status ?? e?.status });
        throw err;
    }
};

export const updateSop = async (id: string, payload: Omit<UpsertSopPayload, 'file_id' | 'file_name'>) => {
    return apiCall(API_ENDPOINTS.sops.update(id), {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
};

export const deleteSop = async (id: string) => {
    return apiCall(API_ENDPOINTS.sops.delete(id), {
        method: 'DELETE',
    });
};

export const cancelSopProcessing = async (fileId: string) => {
    return apiCall(`${API_BASE_URL}/sops/${fileId}/cancel-processing`, {
        method: 'DELETE',
    });
};

// ------------------------------
// Admin Settings
// ------------------------------

export const fetchMaxDailyMessages = async (): Promise<{ value: number | null }> => {
    return apiCall(API_ENDPOINTS.adminSettings.maxDailyMessages);
};

export const updateMaxDailyMessages = async (value: number | null): Promise<{ value: number | null }> => {
    return apiCall(API_ENDPOINTS.adminSettings.maxDailyMessages, {
        method: 'PUT',
        body: JSON.stringify({ value }),
    });
};

// Speech-to-text settings
export const fetchSpeechToTextSetting = async (): Promise<{ enabled: boolean }> => {
    return apiCall(API_ENDPOINTS.adminSettings.speechToText);
};

export const fetchSpeechToTextSettingPublic = async (): Promise<{ enabled: boolean }> => {
    return apiCall(API_ENDPOINTS.settings.speechToText);
};

export const updateSpeechToTextSetting = async (enabled: boolean): Promise<{ enabled: boolean }> => {
    return apiCall(API_ENDPOINTS.adminSettings.speechToText, {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
    });
};

// Allow managers to create users settings
export const fetchAllowManagersCreateUsersSetting = async (): Promise<{ enabled: boolean }> => {
    return apiCall(API_ENDPOINTS.adminSettings.allowManagersCreateUsers);
};

export const updateAllowManagersCreateUsersSetting = async (enabled: boolean): Promise<{ enabled: boolean }> => {
    return apiCall(API_ENDPOINTS.adminSettings.allowManagersCreateUsers, {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
    });
};

// System email settings
export const fetchSystemEmailSetting = async (): Promise<{ email: string }> => {
    return apiCall(API_ENDPOINTS.adminSettings.systemEmail);
};

export const updateSystemEmailSetting = async (email: string): Promise<{ email: string }> => {
    return apiCall(API_ENDPOINTS.adminSettings.systemEmail, {
        method: 'PUT',
        body: JSON.stringify({ email }),
    });
};

export const testSystemEmail = async (): Promise<{ message: string; email_sent: boolean }> => {
    return apiCall(API_ENDPOINTS.adminSettings.testSystemEmail, {
        method: 'POST',
    });
};

// AI Cost tracking functions
export const fetchCurrentMonthAiCosts = async (): Promise<AiCostSummary> => {
    const response = await apiCall(API_ENDPOINTS.aiCosts.currentMonth);
    return response.data;
};

export const fetchMonthlyAiCosts = async (year: number, month: number): Promise<AiCostSummary> => {
    const response = await apiCall(API_ENDPOINTS.aiCosts.monthly(year, month));
    return response.data;
};

export const fetchAiCostTrends = async (days: number = 30): Promise<AiCostTrendsData> => {
    const url = `${API_ENDPOINTS.aiCosts.trends}?days=${days}`;
    const response = await apiCall(url);
    return response.data;
};

export const fetchUserAiCosts = async (userEmail: string, days: number = 30): Promise<UserAiCostSummary> => {
    const url = `${API_ENDPOINTS.aiCosts.user(userEmail)}?days=${days}`;
    const response = await apiCall(url);
    return response.data;
};

export const fetchAiCostsSummary = async (): Promise<AiCostsSummaryData> => {
    const response = await apiCall(API_ENDPOINTS.aiCosts.summary);
    return response.data;
};

export const getStatus = async () => {
	return apiCall(API_ENDPOINTS.documents.status);
};

export const fetchDashboardStats = async () => {
    return apiCall(API_ENDPOINTS.dashboard.stats);
};

export const fetchDocumentUsageStats = async () => {
    return apiCall(API_ENDPOINTS.dashboard.documentUsageStats);
};

export const fetchManagerDashboardStats = async () => {
    return apiCall(API_ENDPOINTS.dashboard.managerStats);
};

export const fetchManagerChatSessions = async (params: { page?: number; pageSize?: number } = {}) => {
    const { page = 1, pageSize = 10 } = params;
    const url = new URL(`${API_BASE_URL}/manager/chat-sessions`);
    url.searchParams.set('page', String(page));
    url.searchParams.set('page_size', String(pageSize));
    return apiCall(url.toString());
};

export const fetchManagerOwnChatSessions = async (params: { page?: number; pageSize?: number } = {}) => {
    const { page = 1, pageSize = 10 } = params;
    const url = new URL(`${API_BASE_URL}/manager/my-chat-sessions`);
    url.searchParams.set('page', String(page));
    url.searchParams.set('page_size', String(pageSize));
    return apiCall(url.toString());
};

export const fetchManagerWorkers = async (params: { page?: number; pageSize?: number; search?: string; status?: string } = {}) => {
    const { page = 1, pageSize = 10, search, status } = params;
    const url = new URL(`${API_BASE_URL}/manager/workers`);
    url.searchParams.set('page', String(page));
    url.searchParams.set('page_size', String(pageSize));
    if (search) url.searchParams.set('search', escapeRegexSpecialChars(search));
    if (status) url.searchParams.set('status', status);
    return apiCall(url.toString());
};

export const createManagerWorker = async (payload: { name: string; email: string }) => {
    return apiCall(`${API_BASE_URL}/manager/workers`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
};

// Manager Chat Sessions APIs
export const createManagerChatSession = async (title?: string): Promise<{ item: ChatSessionListItem }> => {
    return apiCall(`${API_BASE_URL}/manager/chat-sessions`, {
        method: 'POST',
        body: JSON.stringify({ title }),
    });
};


export const getManagerChatSession = async (id: string): Promise<{ item: ChatSessionListItem & { messages: ChatSessionMessage[] } }> => {
    return apiCall(`${API_BASE_URL}/manager/chat-sessions/${id}`);
};

export const appendManagerChatSessionMessages = async (id: string, messages: ChatSessionMessage[]): Promise<{ item: ChatSessionListItem }> => {
    return apiCall(`${API_BASE_URL}/manager/chat-sessions/${id}/messages`, {
        method: 'POST',
        body: JSON.stringify({ messages }),
    });
};

export const renameManagerChatSession = async (id: string, title: string): Promise<{ item: ChatSessionListItem }> => {
    return apiCall(`${API_BASE_URL}/manager/chat-sessions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ title }),
    });
};

export const deleteManagerChatSession = async (id: string): Promise<{ message: string }> => {
    return apiCall(`${API_BASE_URL}/manager/chat-sessions/${id}`, {
        method: 'DELETE',
    });
};

export const fetchManagerChatLogs = async (params: { page?: number; pageSize?: number; search?: string; role?: string } = {}) => {
    const { page = 1, pageSize = 9, search = '', role } = params;
    const url = new URL(`${API_BASE_URL}/manager/chat-logs`);
    url.searchParams.set('page', String(page));
    url.searchParams.set('page_size', String(pageSize));
    if (search) url.searchParams.set('search', escapeRegexSpecialChars(search));
    if (role) url.searchParams.set('role', role);
    return apiCall(url.toString());
};

export const fetchManagerChatLogById = async (id: string) => {
    return apiCall(`${API_BASE_URL}/manager/chat-logs/${id}`);
};

export const fetchManagerProfileStats = async () => {
    return apiCall(`${API_BASE_URL}/manager/profile/stats`);
};

// ------------------------------
// Auth helpers
// ------------------------------

export interface LastLoginResponse { last_login_at: string | null }
export const fetchLastLogin = async (): Promise<LastLoginResponse> => {
    return apiCall(API_ENDPOINTS.auth.lastLogin);
};

export const changePassword = async (currentPassword: string, newPassword: string, confirmPassword: string): Promise<{ message: string }> => {
    // Custom API call for password change that doesn't trigger global logout on 401
    const token = localStorage.getItem('access_token');
    const response = await fetch(API_ENDPOINTS.auth.changePassword, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword, confirm_password: confirmPassword })
    });

    if (!response.ok) {
        const error = await response
            .json()
            .catch(() => ({ detail: 'An error occurred' }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }

    return response.json();
};

// ------------------------------
// User Profile Data
// ------------------------------

export interface UserProfileData {
    id: string;
    email: string;
    full_name: string;
    role: string;
    created_at: string;
    is_active: boolean;
    last_login_at?: string | null;
    assigned_manager?: string | null;
	assigned_manager_name?: string | null;
}

export const fetchUserProfile = async (): Promise<UserProfileData> => {
    return apiCall(API_ENDPOINTS.auth.me);
};

// ------------------------------
// Chat History
// ------------------------------

export type ChatMessage = {
	role: 'user' | 'assistant';
	question?: string;
	answer?: string;
	images?: string[];
	timestamp: string;
};

export const fetchChatHistory = async (): Promise<{ messages: ChatMessage[] }> => {
	return apiCall(API_ENDPOINTS.chat.getHistory);
};

export const saveChatHistory = async (messages: ChatMessage[]) => {
	return apiCall(API_ENDPOINTS.chat.saveHistory, {
		method: 'POST',
		body: JSON.stringify({ messages }),
	});
};

// ------------------------------
// Chat Feedback
// ------------------------------

export type ChatFeedbackType = 'UP' | 'DOWN';

export type ChatFeedbackReason =
	| 'WRONG_ANSWER'
	| 'IRRELEVANT_SOP'
	| 'MISSING_INFORMATION'
	| 'HALLUCINATION'
	| 'TOO_GENERIC'
	| 'OTHER';

export interface ChatFeedbackPayload {
	sessionId: string;
	messageId: string;
	feedbackType: ChatFeedbackType;
	reason?: ChatFeedbackReason;
	/** Optional free text; max length matches `CHAT_FEEDBACK_COMMENT_MAX_LENGTH` (500). */
	comment?: string;
}

export const submitChatFeedback = async (
	payload: ChatFeedbackPayload
): Promise<{ success?: boolean; message?: string }> => {
	return apiCall(API_ENDPOINTS.chat.feedback, {
		method: 'POST',
		body: JSON.stringify(payload),
	});
};

// ------------------------------
// Admin feedback review (GET /admin/feedback)
// ------------------------------

export type AdminFeedbackItem = Record<string, unknown>;

export type AdminFeedbackListResponse = {
	items: AdminFeedbackItem[];
	total: number;
};

export interface FetchAdminFeedbackParams {
	page?: number;
	pageSize?: number;
	search?: string;
	feedbackType?: ChatFeedbackType;
	reason?: ChatFeedbackReason;
	dateFrom?: string;
	dateTo?: string;
}

export const fetchAdminFeedback = async (
	params: FetchAdminFeedbackParams = {}
): Promise<AdminFeedbackListResponse> => {
	const { page = 1, pageSize = 10, search = '', feedbackType, reason, dateFrom, dateTo } = params;
	const url = new URL(API_ENDPOINTS.adminFeedback.list);
	url.searchParams.set('page', String(page));
	url.searchParams.set('page_size', String(pageSize));
	if (search) url.searchParams.set('search', escapeRegexSpecialChars(search));
	if (feedbackType) url.searchParams.set('feedbackType', feedbackType);
	if (reason) url.searchParams.set('reason', reason);
	if (dateFrom) url.searchParams.set('dateFrom', dateFrom);
	if (dateTo) url.searchParams.set('dateTo', dateTo);
	return apiCall(url.toString());
};

// ------------------------------
// Chat Sessions (Worker multi-chat)
// ------------------------------

export type ChatSessionListItem = {
	id: string;
	title: string;
	createdAt: string;
	updatedAt: string;
	lastMessagePreview?: string;
};

/** Matches chat log API pass-through (object or list from stored messages). */
export type SourcedFromField =
	| {
			documentName?: string;
			sectionName?: string;
	  }
	| Array<
			| string
			| {
					documentName?: string;
					sectionName?: string;
			  }
	  >;

/** Collect document names from a sourcedFrom field (object or list shape). */
export function getSourcedFromDocumentNames(
	sourcedFrom: SourcedFromField | undefined,
): string[] {
	if (!sourcedFrom) {
		return [];
	}

	if (Array.isArray(sourcedFrom)) {
		return sourcedFrom
			.map((entry) => {
				if (typeof entry === 'string') {
					return entry.trim();
				}
				return entry.documentName?.trim() ?? '';
			})
			.filter(Boolean);
	}

	return sourcedFrom.documentName ? [sourcedFrom.documentName] : [];
}

export type ChatSessionMessage = {
	role: 'user' | 'assistant';
	text: string;
	images?: string[];
	timestamp: string;
	messageId?: string;
	is_flagged?: boolean;
	flag_type?: string;
	detected_pattern?: string;
	sopSource?: {
		sopName: string;
		sectionTitle: string;
		fileId: string;
		documentContent?: string;
	};
	sourcedFrom?: SourcedFromField;
	pdfUrl?: string;
};

export const createChatSession = async (title?: string): Promise<{ item: ChatSessionListItem }> => {
	return apiCall(API_ENDPOINTS.chatSessions.create, {
		method: 'POST',
		body: JSON.stringify({ title }),
	});
};

export const listChatSessions = async (): Promise<{ items: ChatSessionListItem[]; total: number }> => {
	return apiCall(API_ENDPOINTS.chatSessions.list);
};

export const getChatSession = async (id: string): Promise<{ item: ChatSessionListItem & { messages: ChatSessionMessage[] } }> => {
	return apiCall(API_ENDPOINTS.chatSessions.get(id));
};

export const appendChatSessionMessages = async (id: string, messages: ChatSessionMessage[]): Promise<{ item: ChatSessionListItem }> => {
	return apiCall(API_ENDPOINTS.chatSessions.appendMessages(id), {
		method: 'POST',
		body: JSON.stringify({ messages }),
	});
};

export const renameChatSession = async (id: string, title: string): Promise<{ item: ChatSessionListItem }> => {
	return apiCall(API_ENDPOINTS.chatSessions.rename(id), {
		method: 'PATCH',
		body: JSON.stringify({ title }),
	});
};

export const deleteChatSession = async (id: string): Promise<{ message: string }> => {
	return apiCall(API_ENDPOINTS.chatSessions.delete(id), { method: 'DELETE' });
};

// ------------------------------
// Chat Logs (Admin Reports)
// ------------------------------

export type ChatLogListItem = {
	id: string;
	title: string;
	user: string;
	role: string;
	date: string;
	sopReferenced: string;
	warnings: number;
};

export type ChatLogMessageItem = {
	role: 'user' | 'assistant';
	text: string;
	images?: string[];
	timestamp: string;
	messageId?: string;
	is_flagged?: boolean;
	flag_type?: string;
	detected_pattern?: string;
	sopSource?: {
		sopName: string;
		sectionTitle: string;
		fileId: string;
		documentContent?: string;
	};
	sourcedFrom?: SourcedFromField;
	pdfUrl?: string;
};

export const fetchChatLogs = async (params: { page?: number; pageSize?: number; search?: string; role?: string } = {}) => {
	const { page = 1, pageSize = 9, search = '', role } = params;
	const url = new URL(API_ENDPOINTS.chatLogs.list);
	url.searchParams.set('page', String(page));
	url.searchParams.set('page_size', String(pageSize));
	if (search) url.searchParams.set('search', escapeRegexSpecialChars(search));
	if (role) url.searchParams.set('role', role);
	return apiCall(url.toString());
};

export const fetchChatSessions = async (params: { page?: number; pageSize?: number; search?: string; role?: string } = {}) => {
	const { page = 1, pageSize = 9, search = '', role } = params;
	const url = new URL(API_ENDPOINTS.adminChatSessions.list);
	url.searchParams.set('page', String(page));
	url.searchParams.set('page_size', String(pageSize));
	if (search) url.searchParams.set('search', escapeRegexSpecialChars(search));
	if (role) url.searchParams.set('role', role);
	return apiCall(url.toString());
};

export const fetchChatLogById = async (
	id: string
): Promise<{ id: string; title: string; user: string; role: string; date: string; sopReferenced: string; warnings: number; messages: ChatLogMessageItem[] }> => {
	return apiCall(API_ENDPOINTS.chatLogs.get(id));
};

export const fetchChatSessionById = async (
	id: string
): Promise<{ id: string; title: string; user: string; role: string; date: string; sopReferenced: string; warnings: number; messages: ChatLogMessageItem[] }> => {
	return apiCall(API_ENDPOINTS.chatSessions.get(id));
};

export const createChatLog = async (payload: { title: string; sop_referenced?: string; messages: ChatLogMessageItem[] }) => {
	return apiCall(API_ENDPOINTS.chatLogs.create, {
		method: 'POST',
		body: JSON.stringify(payload),
	});
};

export const createSessionChatLog = async (payload: { title?: string; sop_referenced?: string; messages: ChatLogMessageItem[]; session_id?: string }) => {
	return apiCall(API_ENDPOINTS.chatLogs.createSession, {
		method: 'POST',
		body: JSON.stringify(payload),
	});
};

export const exportChatLogsCsv = async (params: { search?: string; role?: string } = {}): Promise<Blob> => {
	const { search = '', role } = params;
	const url = new URL(API_ENDPOINTS.chatLogs.export);
	if (search) url.searchParams.set('search', escapeRegexSpecialChars(search));
	if (role) url.searchParams.set('role', role);

	const response = await fetch(url.toString(), {
		method: 'GET',
		headers: getAuthHeaders(),
	});
	if (!response.ok) {
		throw new Error('Failed to export chat logs');
	}
	return response.blob();
};

// Admin Chat Sessions (for admin reports screen if you later swap to sessions)
export const adminFetchChatSessions = async (params: { page?: number; pageSize?: number; search?: string; role?: string } = {}) => {
	const { page = 1, pageSize = 10, search = '', role } = params;
	const url = new URL(API_ENDPOINTS.adminChatSessions.list);
	url.searchParams.set('page', String(page));
	url.searchParams.set('page_size', String(pageSize));
	if (search) url.searchParams.set('search', escapeRegexSpecialChars(search));
	if (role) url.searchParams.set('role', role);
	return apiCall(url.toString());
};

export const adminGetChatSession = async (id: string) => {
	return apiCall(API_ENDPOINTS.adminChatSessions.get(id));
};

export const adminExportChatSessionsCsv = async (params: { search?: string; role?: string } = {}): Promise<Blob> => {
	const { search = '', role } = params;
	const url = new URL(API_ENDPOINTS.adminChatSessions.export);
	if (search) url.searchParams.set('search', escapeRegexSpecialChars(search));
	if (role) url.searchParams.set('role', role);
	const response = await fetch(url.toString(), { method: 'GET', headers: getAuthHeaders() });
	if (!response.ok) throw new Error('Failed to export chat sessions');
	return response.blob();
};

// ------------------------------
// Forgot Password Functions
// ------------------------------

export interface ForgotPasswordSendLinkRequest {
	email: string;
}

export interface ForgotPasswordSendLinkResponse {
	message: string;
	email_sent: boolean;
}

export const forgotPasswordSendLink = async (email: string): Promise<ForgotPasswordSendLinkResponse> => {
	return apiCall(API_ENDPOINTS.auth.forgotPasswordSendLink, {
		method: 'POST',
		body: JSON.stringify({ email }),
	});
};

export interface ForgotPasswordResetRequest {
	reset_token: string;
	new_password: string;
	confirm_password: string;
}

export interface ForgotPasswordResetResponse {
	message: string;
}

export const forgotPasswordReset = async (
	resetToken: string,
	newPassword: string,
	confirmPassword: string
): Promise<ForgotPasswordResetResponse> => {
	return apiCall(API_ENDPOINTS.auth.forgotPasswordReset, {
		method: 'POST',
		body: JSON.stringify({
			reset_token: resetToken,
			new_password: newPassword,
			confirm_password: confirmPassword,
		}),
	});
};

export const setPassword = async (
	setupToken: string,
	newPassword: string,
	confirmPassword: string
): Promise<ForgotPasswordResetResponse> => {
	return apiCall(API_ENDPOINTS.auth.setPassword, {
		method: 'POST',
		body: JSON.stringify({
			setup_token: setupToken,
			new_password: newPassword,
			confirm_password: confirmPassword,
		}),
	});
};

// ------------------------------
// Notification API functions
// ------------------------------

export const fetchNotifications = async (): Promise<NotificationResponse> => {
	return apiCall(API_ENDPOINTS.notifications.list, {
		method: 'GET',
	});
};

export const markNotificationAsRead = async (notificationId: string): Promise<NotificationResponse> => {
	return apiCall(API_ENDPOINTS.notifications.markRead(notificationId), {
		method: 'PATCH',
	});
};

export const markAllNotificationsAsRead = async (): Promise<NotificationResponse> => {

	return apiCall(API_ENDPOINTS.notifications.markAllRead, {
		method: 'PATCH',
	});
};

export const fetchNotificationSettings = async (): Promise<NotificationResponse> => {
	return apiCall(API_ENDPOINTS.notifications.settings, {
		method: 'GET',
	});
};

// ------------------------------
// Test Notification API functions (Admin only)
// ------------------------------

export interface TestNotificationStats {
	total_notifications: number;
	total_unread: number;
	by_type: Array<{
		_id: string;
		count: number;
		unread_count: number;
	}>;
	by_role: Array<{
		_id: string;
		count: number;
	}>;
	last_updated: string;
}

export const sendTestSopAddedNotification = async (data: {
	sop_title?: string;
	sop_id?: string;
}): Promise<{ success: boolean; message: string; [key: string]: any }> => {
	return apiCall(API_ENDPOINTS.testNotifications.sendSopAdded, {
		method: 'POST',
		body: JSON.stringify(data),
	});
};

export const sendTestUserOnboardedNotification = async (data: {
	manager_name?: string;
	onboarded_user_name?: string;
	onboarded_user_email?: string;
	onboarded_user_role?: string;
}): Promise<{ success: boolean; message: string; [key: string]: any }> => {
	return apiCall(API_ENDPOINTS.testNotifications.sendUserOnboarded, {
		method: 'POST',
		body: JSON.stringify(data),
	});
};

export const sendTestGeneralNotification = async (data: {
	title?: string;
	message?: string;
	type?: string;
}): Promise<{ success: boolean; message: string; [key: string]: any }> => {
	return apiCall(API_ENDPOINTS.testNotifications.sendGeneral, {
		method: 'POST',
		body: JSON.stringify(data),
	});
};

export const getTestNotificationStats = async (): Promise<{
	success: boolean;
	stats: TestNotificationStats;
}> => {
	return apiCall(API_ENDPOINTS.testNotifications.getStats, {
		method: 'GET',
	});
};

export const updateNotificationSettings = async (enabled: boolean): Promise<NotificationResponse> => {
	return apiCall(API_ENDPOINTS.notifications.settings, {
		method: 'PUT',
		body: JSON.stringify({ enabled }),
	});
};

export const fetchAdminNotificationSettings = async (): Promise<NotificationResponse> => {
	return apiCall(API_ENDPOINTS.adminSettings.notifications, {
		method: 'GET',
	});
};

export const updateAdminNotificationSettings = async (enabled: boolean): Promise<NotificationResponse> => {
	return apiCall(API_ENDPOINTS.adminSettings.notifications, {
		method: 'PUT',
		body: JSON.stringify({ enabled }),
	});
};

// FCM Token Management
export const registerFCMToken = async (token: string): Promise<{ success: boolean; message: string }> => {
	return apiCall(`${API_BASE_URL}/notifications/register-token`, {
		method: 'POST',
		body: JSON.stringify({ token }),
	});
};

export const unregisterFCMToken = async (): Promise<{ success: boolean; message: string }> => {
	return apiCall(`${API_BASE_URL}/notifications/unregister-token`, {
		method: 'DELETE',
	});
};
